import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
	debounceTime,
	distinctUntilChanged,
	EMPTY,
	from,
	map,
	Observable,
	Subject,
	switchMap,
	takeUntil,
	tap,
} from 'rxjs';
import { AppRoutes } from 'src/app/app-routes';
import { CustomerDataUI, ValidateCustomerDataUI } from 'src/app/interfaces/customer-data.interface';
import {
	InitialParametersUI,
	SalesLevelModel,
} from 'src/app/interfaces/initial-parameters.interface';
import { ClickEventTags, PageEventTags } from 'src/app/services/gtm/gtm-types';
import { GtmService } from 'src/app/services/gtm/gtm.service';
import { LoaderService } from 'src/app/services/loader/loader.service';
import { RecaptchaService } from 'src/app/services/recaptcha/recaptcha.service';
import { StepperService } from 'src/app/services/stepper/stepper.service';
import { StoreService } from 'src/app/services/store/store.service';
import { Links, Patterns, RecaptchaActions } from 'src/app/utils/constants';
import { mapper } from 'src/app/utils/mapper';
import {
	buildRequestGenerateOtp,
	buildUrlSimulator,
	emailRegexValidator,
	ensureUserRut,
	findSelectedSalesRange,
	isLegalEntity,
	mapToGtmExtras,
	naturalPersonValidator,
	phoneValidationsRegex,
	rutValidator,
} from 'src/app/utils/utils';

@Component({
	selector: 'app-company-data',
	templateUrl: './company-data.component.html',
	styleUrls: ['./company-data.component.scss'],
})
export class CompanyDataComponent implements OnInit, OnDestroy {
	readonly parameters$ = this.storeService.initialParameters$;

	companyDataForm: FormGroup;

	private destroy$ = new Subject<void>();

	constructor(
		private readonly recaptchaService: RecaptchaService,
		private readonly loaderService: LoaderService,
		private readonly storeService: StoreService,
		private readonly fb: FormBuilder,
		private readonly stepperService: StepperService,
		private readonly router: Router,
		private readonly destroyRef: DestroyRef,
		private readonly gtmService: GtmService
	) {}

	get companyRutControl() {
		return this.companyDataForm.controls.companyRut;
	}

	get userRutControl() {
		return this.companyDataForm.get('userRut');
	}

	get phoneControl() {
		return this.companyDataForm.controls.phoneNumber;
	}
	get emailControl() {
		return this.companyDataForm.controls.email;
	}

	ngOnInit(): void {
		this.initForm();
		this.handleCompanyRutChanges();
		this.gtmService.handlerEvent(PageEventTags.companyData);
	}

	initForm(): void {
		this.companyDataForm = this.fb.nonNullable.group({
			companyRut: ['', [Validators.required, Validators.maxLength(12), rutValidator()]],
			phoneNumber: [
				'',
				[Validators.required, Validators.minLength(12), Validators.pattern(Patterns.phone), phoneValidationsRegex()],
			],
			email: ['', [Validators.required, Validators.email, emailRegexValidator()]],
			salesLevel: ['', [Validators.required]],
			privacyTerms: [false, Validators.requiredTrue],
		});

		this.setupErrorTrigger(this.companyRutControl);
		this.setupErrorTrigger(this.emailControl);
	}

	private createUserRutControl(): AbstractControl {
		return this.fb.nonNullable.control('', {
			validators: [
				Validators.required,
				Validators.maxLength(12),
				rutValidator(),
				naturalPersonValidator(),
			],
		});
	}

	private handleCompanyRutChanges(): void {
		this.companyRutControl.valueChanges
			.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
			.subscribe(rut => {
				if (isLegalEntity(rut)) {
					if (!this.companyDataForm.contains('userRut')) {
						const userRut = this.createUserRutControl();
						this.companyDataForm.addControl('userRut', userRut);
						this.setupErrorTrigger(userRut);
					}
					return;
				}

				if (this.companyDataForm.contains('userRut')) {
					this.companyDataForm.removeControl('userRut');
				}
			});
	}

	private setupErrorTrigger(control: AbstractControl, delayMs = 1000): void {
		control.statusChanges
			.pipe(debounceTime(delayMs), distinctUntilChanged(), takeUntil(this.destroy$))
			.subscribe(status => {
				if (status === 'INVALID') {
					control.markAsTouched({ onlySelf: true });
				}
			});
	}

	onOpenPrivacyTerms(): void {
		window.open(Links.legal.privacyPolicyBCI, '_blank', 'noopener,noreferrer');
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	onSendDataForValidation(parameters: InitialParametersUI): void {
		if (this.companyDataForm.invalid) {
			this.companyDataForm.markAllAsTouched();
			return;
		}
		this.validateFlow$(parameters).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
	}

	private validateFlow$(parameters: InitialParametersUI): Observable<void> {
		const formData: CustomerDataUI = this.companyDataForm.getRawValue();

		return this.pushGtm$(parameters.salesLevels, formData).pipe(
			switchMap(() => this.recaptchaService.executeAction(RecaptchaActions.checkPreapprovalValidity)),
			map(token => mapper.requestValidateData(ensureUserRut(formData), token)),
			switchMap(request => this.storeService.validateData(request)),
			tap(() => this.saveDataAndAdvance(ensureUserRut(formData))),
			switchMap(validations =>
				this.handleValidationResult$(validations, parameters.simulatorUrl, formData.companyRut)
			),
			map(() => void 0)
		);
	}

	private pushGtm$(salesLevels: SalesLevelModel[], formData: CustomerDataUI): Observable<void> {
		const selectedRange = findSelectedSalesRange(salesLevels, formData.salesLevel);

		return from(mapToGtmExtras(formData.companyRut, formData.email, selectedRange)).pipe(
			tap(extras => this.gtmService.setExtras(extras)),
			tap(() => this.gtmService.handlerEvent(ClickEventTags.validateData)),
			map(() => void 0)
		);
	}

	private saveDataAndAdvance(formData: CustomerDataUI): void {
		this.stepperService.nextStep();
		this.storeService.setCustomerData(formData);
	}

	private handleValidationResult$(
		validations: ValidateCustomerDataUI,
		simulatorUrl: string,
		companyRut: string
	): Observable<void> {
		if (!validations.approvedRisk) {
			this.loaderService.show();
			window.location.assign(buildUrlSimulator(simulatorUrl, validations.uuid, validations.id));
			return EMPTY;
		}

		if (!validations.applyCte) {
			return this.storeService.generateSendOtp(buildRequestGenerateOtp(companyRut)).pipe(
				tap(() => this.router.navigate([AppRoutes.OTP])),
				map(() => void 0)
			);
		}

		return this.storeService.generateConsent(companyRut).pipe(
			tap(() => this.router.navigate([AppRoutes.TAX_FOLDER])),
			map(() => void 0)
		);
	}
}
